import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Card } from '../../components/common/Card';
import { GameForm } from '../../components/games/GameForm';
import { gamesApi } from '../../api/games.api';
import { CreateGameRequest } from '../../types/game.types';
import toast from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../../components/common/Button';

export const CreateGame: React.FC = () => {
  const navigate = useNavigate();

  const createGameMutation = useMutation({
    mutationFn: gamesApi.createGame,
    onSuccess: (response) => {
      toast.success('Game created successfully!');
      
      if (response.data.apiKey) {
        navigator.clipboard.writeText(response.data.apiKey);
        toast.success('API key copied to clipboard');
      }
      
      navigate('/games');
    },
    onError: () => {
      toast.error('Failed to create game');
    },
  });

  const createWithSamplePayload = async () => {
    const bundleId = window.prompt('Enter bundleId', 'com.example.bubbletea')?.trim();
    if (!bundleId) return;

    const payload: CreateGameRequest = {
      bundleId,
      name: 'Bubble Tea',
      settings: {
        defaultCurrency: 'USD',
        allowedCurrencies: ['USD', 'EUR'],
        subscriptionPlans: {
          weekly: {
            amount: 499,
            currency: 'USD',
            interval: 'week',
            intervalCount: 1,
            oppo: {
              subscribeToken: 'sub_weekly_created_token',
              renewToken: 'sub_weekly_renewed_token',
              cancelToken: 'sub_weekly_cancelled_token',
            },
            vivo: {
              subscribeToken: 'sub_weekly_created_token',
              renewToken: 'sub_weekly_renewed_token',
              cancelToken: 'sub_weekly_cancelled_token',
            },
          },
          monthly: {
            amount: 1499,
            currency: 'USD',
            interval: 'month',
            intervalCount: 1,
            oppo: {
              subscribeToken: 'sub_monthly_created_token',
              renewToken: 'sub_monthly_renewed_token',
              cancelToken: 'sub_monthly_cancelled_token',
            },
            vivo: {
              subscribeToken: 'sub_monthly_created_token',
              renewToken: 'sub_monthly_renewed_token',
              cancelToken: 'sub_monthly_cancelled_token',
            },
          },
          yearly: {
            amount: 9999,
            currency: 'USD',
            interval: 'year',
            intervalCount: 1,
            oppo: {
              subscribeToken: 'sub_yearly_created_token',
              renewToken: 'sub_yearly_renewed_token',
              cancelToken: 'sub_yearly_cancelled_token',
            },
            vivo: {
              subscribeToken: 'sub_yearly_created_token',
              renewToken: 'sub_yearly_renewed_token',
              cancelToken: 'sub_yearly_cancelled_token',
            },
          },
          lifetime: {
            amount: 2499,
            currency: 'USD',
            oppo: { adjustToken: 'prod_lifetime_token' },
            vivo: { adjustToken: 'prod_lifetime_token' },
          },
        },
        purchaseBundles: {
          starter_pack: {
            amount: 299,
            currency: 'USD',
            oppo: { adjustToken: 'prod_lifetime_token' },
            vivo: { adjustToken: 'prod_lifetime_token' },
          },
          mega_pack: {
            amount: 1999,
            currency: 'USD',
            oppo: { adjustToken: 'prod_lifetime_token' },
            vivo: { adjustToken: 'prod_lifetime_token' },
          },
        },
        rateLimits: { requests: 100, window: 900000 },
      },
      adjustConfig: {
        appTokens: {
          default: 'your_game_adjust_app_token',
          oppo: 'your_game_adjust_app_token',
          vivo: 'your_game_adjust_app_token',
        },
        s2sAuthToken: 'your_s2s_auth_token',
        subscriptionEvents: {
          created: 'sub_created_token',
          renewed: 'sub_renewed_token',
          cancelled: 'sub_cancelled_token',
        },
        productEvents: {
          coins_500: 'prod_coins_500_token',
          starter_pack: 'prod_starter_pack_token',
        },
      },
    };

    await createGameMutation.mutateAsync(payload);
  };

  const handleSubmit = async (data: CreateGameRequest) => {
    await createGameMutation.mutateAsync(data);
  };

  return (
    <div>
      <div className="mb-6 flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/games')}
          icon={<ArrowLeft className="w-4 h-4" />}
        >
          Back to Games
        </Button>

        <Button
          variant="secondary"
          size="sm"
          onClick={createWithSamplePayload}
          disabled={createGameMutation.isPending}
        >
          Create sample "Bubble Tea"
        </Button>
      </div>

      <div className="max-w-2xl">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Create New Game</h1>
        <Card>
          <GameForm
            onSubmit={handleSubmit}
            isLoading={createGameMutation.isPending}
          />
        </Card>
      </div>
    </div>
  );
};